import { useState } from "react";
import { Link2OffIcon, LinkIcon } from "lucide-react";
import { useNavigate } from "react-router";

import { JobLinkPickerDialog } from "./JobLinkPickerDialog";
import {
  Column,
  ConfirmationDialog,
  JobStatusBadge,
  Row,
  Text,
} from "@/components";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useCanLinkJobs } from "@/hooks/useCanLinkJobs";
import { useCreateAddJobLink } from "@/hooks/createAdds/useCreateAddJobLink";
import { useDeleteRemoveJobLink } from "@/hooks/deleteRemoves/useDeleteRemoveJobLink";
import { useGetLinkedJobs } from "@/hooks/queries/useGetLinkedJobs";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { Job } from "@/types";

// A job's link family ("versions of the same job"). Only the authorized
// linkers see any of this. `guac` enforces the writes, this just hides
// the UI.
export const JobLinksPopover = ({ job }: { job: Job }) => {
  /* React Hooks */
  const navigate = useNavigate();
  const canLink = useCanLinkJobs();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isUnlinkConfirmOpen, setIsUnlinkConfirmOpen] = useState(false);

  /* Database CRUD Operations */
  // Read. Most jobs are unlinked, so skip the fetch until the job is known
  // to have a group or the user actually opens the popover. (The cached
  // job.jobLinkGroupId can be stale right after in-popover linking. the
  // open-state arm covers that.) Non-linkers never render this, so never
  // fetch for them either.
  const { data: linkedJobs } = useGetLinkedJobs(job.id, {
    enabled: canLink && (!!job.jobLinkGroupId || isPopoverOpen || isPickerOpen),
  });
  // Create
  const {
    mutate: linkJob,
    isPending: isLinkPending,
    isError: isLinkError,
    reset: resetLink,
  } = useCreateAddJobLink();
  // Delete
  const { mutate: unlinkJob, isPending: isUnlinkPending } =
    useDeleteRemoveJobLink();

  /* Helpers */
  const linkCount = linkedJobs?.length ?? 0;

  /* Handlers */
  const handleOpenPicker = () => {
    mixpanelUtils.trackButtonClick("Link job", MIXPANEL_SCREENS.JOB);
    // The mutation's error state outlives the (conditionally mounted)
    // picker — clear it so a past failure doesn't greet the next open.
    resetLink();
    setIsPopoverOpen(false);
    setIsPickerOpen(true);
  };

  const handleLinkConfirm = (targetJobId: string) => {
    linkJob(
      { jobId: job.id, targetJobId },
      {
        onSuccess: () => {
          setIsPickerOpen(false);
        },
      },
    );
  };

  const handleUnlinkConfirm = () => {
    unlinkJob(
      { jobId: job.id },
      {
        onSuccess: () => {
          setIsUnlinkConfirmOpen(false);
        },
      },
    );
  };

  /* Render */
  const renderLinkedJobs = () => {
    if (linkCount === 0) {
      return (
        <Text className="text-sm font-normal text-slate-500">
          Not linked to any jobs. Linked jobs are versions of the same job - for
          example, the same role reopened for a later hiring day.
        </Text>
      );
    }
    return (
      <Column className="gap-1">
        {(linkedJobs ?? []).map((linkedJob) => (
          <Button
            key={linkedJob.id}
            className="h-auto justify-between rounded-md px-2 py-1.5"
            onClick={() => {
              setIsPopoverOpen(false);
              void navigate(
                `/companies/${linkedJob.companyId}/jobs/${linkedJob.id}`,
              );
            }}
            variant="ghost"
          >
            <Text className="truncate">{linkedJob.title}</Text>
            <JobStatusBadge
              className="shrink-0"
              pausedAt={linkedJob.pausedAt}
              pauseReason={linkedJob.pauseReason}
              pauseType={linkedJob.pauseType}
              status={linkedJob.status}
            />
          </Button>
        ))}
      </Column>
    );
  };

  if (!canLink) {
    return null;
  }

  return (
    <>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  className="h-8 rounded-full text-xs"
                  variant="outlineSecondary"
                >
                  <LinkIcon className="h-3.5 w-3.5" />
                  {linkCount > 0 ? `(${linkCount.toString()})` : null}
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent>Linked jobs</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <PopoverContent align="end" className="flex w-80 flex-col gap-4">
          {renderLinkedJobs()}
          <Row className="justify-end gap-2">
            {linkCount > 0 && (
              <Button
                className="rounded-full"
                disabled={isUnlinkPending}
                onClick={() => {
                  mixpanelUtils.trackButtonClick(
                    "Unlink job",
                    MIXPANEL_SCREENS.JOB,
                  );
                  setIsPopoverOpen(false);
                  setIsUnlinkConfirmOpen(true);
                }}
                variant="secondary"
              >
                <Link2OffIcon className="h-3.5 w-3.5" />
                Unlink this job
              </Button>
            )}
            <Button className="rounded-full" onClick={handleOpenPicker}>
              <LinkIcon className="h-3.5 w-3.5" />
              Link a job
            </Button>
          </Row>
        </PopoverContent>
      </Popover>
      {/* Mounted only while open: unmounting resets the picker's filter and
          selection between uses, and stops its queries when closed. */}
      {isPickerOpen && (
        <JobLinkPickerDialog
          isError={isLinkError}
          isLoading={isLinkPending}
          job={job}
          onConfirm={handleLinkConfirm}
          onOpenChange={setIsPickerOpen}
          open={isPickerOpen}
        />
      )}
      <ConfirmationDialog
        description={
          "This removes the job from its link group. The other jobs in the group stay linked to each other."
        }
        isLoading={isUnlinkPending}
        onConfirm={handleUnlinkConfirm}
        onOpenChange={setIsUnlinkConfirmOpen}
        open={isUnlinkConfirmOpen}
        title="Unlink this job?"
      />
    </>
  );
};
